"use client";

import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { toast } from "sonner";

export default function SubmitAITool() {
  const [formData, setFormData] = useState({
    websiteName: "",
    websiteUrl: "",
    email: "",
    description: "",
    pricing: "",
    additionalInfo: ""
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await fetch('/api/submit-tool', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });
      
      const data = await response.json();
      
      if (response.ok) {
        toast.success("Thank you for your submission! We'll review it shortly.");
        setFormData({
          websiteName: "",
          websiteUrl: "",
          email: "",
          description: "",
          pricing: "",
          additionalInfo: ""
        });
      } else {
        toast.error(data.error || "Failed to submit. Please try again.");
      }
    } catch (error) {
      toast.error("An error occurred. Please try again later.");
      console.error("Submission error:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <section className="bg-zinc-900 py-12 md:py-16 lg:py-20 px-4 rounded-bl-[25px] md:rounded-bl-[35px] lg:rounded-bl-[50px] rounded-br-[25px] md:rounded-br-[35px] lg:rounded-br-[50px]">
        <div className="container mx-auto text-center max-w-3xl px-4">
          <h1 className="text-[32px] md:text-[45px] lg:text-[60px] font-medium mb-4 md:mb-6 text-white leading-[1.1]">
            Submit Your AI Tool
          </h1>
          <p className="text-[16px] text-zinc-300 mb-6 md:mb-8 leading-relaxed">
            Have an AI tool that you'd like to share with our community? Submit it here for review.
          </p>
        </div>
      </section>

      <div className="max-w-[730px] mx-auto px-4 py-8 md:py-10 lg:py-12">
        <div className="space-y-8">
          <div className="bg-zinc-50 border border-zinc-200 rounded-[15px] p-6 md:p-8">
            <h2 className="text-xl md:text-2xl font-medium mb-4">Disclosure of Terms</h2>
            <div className="space-y-4 text-[16px] text-[#6E7A84]">
              <p>All submissions undergo a comprehensive review. We retain the right to reject or withdraw any tool from our platform if we deem it unsuitable for our users, regardless of when this determination is made.</p>
              <p>Please be aware that we do not accept NSFW tools.</p>
              
              <h3 className="text-lg font-medium text-zinc-900 mt-6 mb-2">How to get your AI tool listed faster:</h3>
              <ul className="list-disc pl-6 space-y-2">
                <li>Clarity is key. Ensure your product or service description is easily understandable. If an average user can't grasp its purpose within 5 seconds, it won't be included in our directory.</li>
                <li>Provide original descriptions. Copied content from other websites will result in your tool being deprioritized or potentially rejected. Unique descriptions help us process your submission faster, avoiding delays caused by us having to rewrite it.</li>
              </ul>

              <h3 className="text-lg font-medium text-zinc-900 mt-6 mb-2">Submissions must be from established websites that display:</h3>
              <ul className="list-disc pl-6 space-y-2">
                <li>Clear privacy policy</li>
                <li>Terms of service</li>
                <li>Company/entity information</li>
                <li>Professional domain name</li>
              </ul>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-4">
              <div className="grid gap-4">
                <div className="space-y-2">
                  <Label htmlFor="websiteName" className="text-[16px]">Website Name *</Label>
                  <Input
                    id="websiteName"
                    value={formData.websiteName}
                    onChange={(e) => setFormData({ ...formData, websiteName: e.target.value })}
                    className="h-12 rounded-[12px] border-[#A4AFB7] text-[16px]"
                    placeholder="Tool Name"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="websiteUrl" className="text-[16px]">Website URL *</Label>
                  <Input
                    id="websiteUrl"
                    type="url"
                    placeholder="https://example.com"
                    value={formData.websiteUrl}
                    onChange={(e) => setFormData({ ...formData, websiteUrl: e.target.value })}
                    className="h-12 rounded-[12px] border-[#A4AFB7] text-[16px]"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email" className="text-[16px]">Email *</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="joe@example.com"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="h-12 rounded-[12px] border-[#A4AFB7] text-[16px]"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description" className="text-[16px]">Tool Description *</Label>
                  <Textarea
                    id="description"
                    placeholder="A short description of what your AI tool does."
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="min-h-[120px] rounded-[12px] border-[#A4AFB7] text-[16px]"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-[16px]">Pricing *</Label>
                  <RadioGroup
                    value={formData.pricing}
                    onValueChange={(value) => setFormData({ ...formData, pricing: value })}
                    className="grid grid-cols-2 gap-4"
                    required
                  >
                    {["Free", "Freemium", "Paid", "Free Trial"].map((option) => (
                      <div key={option} className="flex items-center space-x-2">
                        <RadioGroupItem value={option} id={option} />
                        <Label htmlFor={option} className="text-[16px]">{option}</Label>
                      </div>
                    ))}
                  </RadioGroup>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="additionalInfo" className="text-[16px]">Additional Information (Optional)</Label>
                  <Textarea
                    id="additionalInfo"
                    placeholder="YouTube video URL, GitHub, logo, icon, assets, etc."
                    value={formData.additionalInfo}
                    onChange={(e) => setFormData({ ...formData, additionalInfo: e.target.value })}
                    className="min-h-[120px] rounded-[12px] border-[#A4AFB7] text-[16px]"
                  />
                </div>
              </div>
            </div>

            <Button
              type="submit"
              className="w-full rounded-[50px] bg-[#B8EF52] text-zinc-900 hover:bg-[#a5d649] h-12 md:h-14 text-[16px]"
              disabled={isSubmitting}
            >
              {isSubmitting ? "Submitting..." : "Submit AI Tool"}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}